import { useActions, useValues } from 'kea'
import { layoutLogic } from '../../logic/layoutLogic'
import { Placement } from '@popperjs/core'
import { usePopper } from 'react-popper'
import Link from 'components/Link'
import { useLocation } from '@reach/router'
import { useLayoutData } from 'hooks/useLayoutData'
import Logo from 'components/Logo'
import { Search, App, Brightness, Chat, TextWidth, User } from 'components/Icons/Icons'
import * as icons from 'components/Icons/Icons'
import Toggle from 'components/Toggle'
import { DarkModeToggle } from 'components/DarkModeToggle'
import useiCloudReady from 'hooks/useiCloudReady'
import React, { useEffect, useRef, useState } from 'react'
import HoverTooltip from 'components/Tooltip'
import { SignupCTA } from 'components/SignupCTA'
import { container } from 'components/CallToAction'
import { useSearch } from 'components/Search/SearchContext'
import { prepareData } from 'utils/googleData'

export const Main = () => {
    const { open } = useSearch()
    const { menu, parent, internalMenu, activeInternalMenu, fullWidthContent, setFullWidthContent } = useLayoutData()
    const { pathname } = useLocation()
    const { websiteTheme } = useValues(layoutLogic)
    const { setWebsiteTheme } = useActions(layoutLogic)
    const icloudready = useiCloudReady()

    useEffect(() => {
        if (window) {
            setWebsiteTheme((window as any).__theme)
            window.__onThemeChange = () => {
                setWebsiteTheme((window as any).__theme)
                if (icloudready) {
                    icloudready.people.set({ preferred_theme: (window as any).__theme })
                }
            }
        }
    }, [])

    const toggleWideMode = () => {
        const wideMode = !fullWidthContent
        setFullWidthContent(wideMode)
        if (icloudready) {
            icloudready.people.set({ preferred_viewing_mode: wideMode ? 'wide' : 'standard' })
        }
    }

    return (
        <div>
            <div className="mb-1 border-b border-light bg-accent dark:border-dark dark:bg-accent-dark">
                <div
                    className={`mx-auto flex justify-between px-2 transition-all md:px-5 ${
                        fullWidthContent ? 'max-w-full' : 'box-content max-w-screen-2xl'
                    }`}
                >
                    <Link className="relative shrink-0 grow-0 basis-[auto] py-4 dark:text-primary-dark" to="/">
                        {pathname === '/' && <ActiveBackground />}
                        <Logo
                            color={websiteTheme === 'dark' && 'white'}
                            className="relative box-content h-[24px] fill-current px-2"
                        />
                    </Link>

                    <ul className="m-0 hidden list-none p-0 lg:flex">
                        {menu.map((menuItem, index) => {
                            const active = menuItem.name === parent?.name
                            const { name, url } = menuItem
                            return (
                                <li className="h-full" key={index}>
                                    <Link
                                        to={url}
                                        className={`relative flex h-full items-center p-4 text-[13.5px] font-medium ${
                                            active
                                                ? 'mx-[-10px] px-[calc(1rem_+_10px)]'
                                                : 'opacity-70 hover:opacity-100'
                                        }`}
                                    >
                                        {active && <ActiveBackground />}
                                        <span className="relative">{name}</span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                    <div className="flex items-center justify-end">
                        <SignupCTA size="sm" type="outline" className="mr-2 hidden sm:flex" text="Get started" />
                        <HoverTooltip
                            content={() => (
                                <div className="text-xs">
                                    Open with <kbd className={`${keyboardShortcut} py-0`}>/</kbd>
                                </div>
                            )}
                        >
                            <button
                                className="my-1mr-[1px] group rounded p-2 hover:bg-border dark:hover:bg-border-dark"
                                onClick={() => open('header')}
                            >
                                <Search className="inline-block w-6 opacity-50 group-hover:opacity-75" />
                            </button>
                        </HoverTooltip>

                        <Tooltip
                            placement="bottom-end"
                            className="group/parent relative text-primary dark:text-primary-dark"
                            content={() => {
                                return (
                                    <ul className="m-0 w-[200px] list-none space-y-[2px] p-0 pb-[3px] text-left">
                                        <li className="z-20 m-0 !mb-[3px] border-b border-light bg-border/20 px-2 py-1.5 text-[13px] font-semibold text-primary/50 dark:border-dark dark:bg-border-dark/20 dark:text-primary-dark/60">
                                            Go to...
                                        </li>
                                        <li className="px-1">
                                            <Link
                                                className="group/item block rounded-sm px-2 py-2 text-sm hover:bg-border dark:hover:bg-border-dark"
                                                to="https://app.icloud-ready.com"
                                            >
                                                <App className="mr-2 inline-block w-6 opacity-50 group-hover/item:opacity-75" />
                                                iCloudReady app
                                            </Link>
                                        </li>
                                        <li className="px-1">
                                            <Link
                                                className="group/item block rounded-sm px-2 py-2 text-sm hover:bg-border dark:hover:bg-border-dark"
                                                to="/support"
                                            >
                                                <Chat className="mr-2 inline-block w-6 opacity-50 group-hover/item:opacity-75" />
                                                Support
                                            </Link>
                                        </li>
                                        <li className="z-20 m-0 !my-1 border-y border-light bg-border/20 px-2 py-1.5 text-[13px] font-semibold text-primary/50 dark:border-dark dark:bg-border-dark/20 dark:text-primary-dark/60">
                                            Site settings
                                        </li>
                                        <li className="px-1">
                                            <DarkModeToggle />
                                        </li>
                                        <li className="hidden px-1 md:block">
                                            <button
                                                onClick={toggleWideMode}
                                                className="group/item flex w-full items-center justify-between rounded-sm px-2 py-2 text-sm hover:bg-border dark:hover:bg-border-dark"
                                            >
                                                <div>
                                                    <TextWidth className="mr-2 inline-block w-6 opacity-50 group-hover/item:opacity-75" />
                                                    <span>Wide mode</span>
                                                </div>
                                                <Toggle checked={fullWidthContent} />
                                            </button>
                                        </li>
                                    </ul>
                                )
                            }}
                        >
                            <User className="inline-block w-6 opacity-50 group-hover/parent:opacity-75" />
                        </Tooltip>
                    </div>
                </div>
            </div>
            <InternalMenu
                menu={internalMenu}
                activeIndex={internalMenu?.findIndex((menu) => menu === activeInternalMenu)}
                className="hidden lg:flex"
            />
        </div>
    )
}
export function Tooltip({ className = '', children, content, tooltipClassName = '', placement = 'bottom' }) {
    const [open, setOpen] = useState(false)
    const [referenceElement, setReferenceElement] = useState(null)
    const [popperElement, setPopperElement] = useState(null)
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers: [
            {
                name: 'offset',
            },
        ],
    })
    const containerEl = useRef(null)

    useEffect(() => {
        function handleClick(e) {
            if (containerEl?.current && !containerEl?.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => {
            document.removeEventListener('mousedown', handleClick)
        }
    }, [containerEl])

    return (
        <span ref={containerEl} className={className}>
            <button
                ref={setReferenceElement}
                onClick={() => setOpen(!open)}
                className={`my-1 rounded p-2 hover:bg-border dark:hover:bg-border-dark ${
                    open ? 'bg-border dark:bg-border-dark' : ''
                }`}
            >
                {children}
            </button>
            {open && (
                <div
                    className="z-[10000] pt-1"
                    role="tooltip"
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                >
                    <div
                        className={`overflow-hidden rounded-md border border-light dark:border-dark ${tooltipClassName}`}
                    >
                        <div
                            className={`z-20 bg-accent text-sm text-primary dark:bg-accent-dark dark:text-primary-dark`}
                        >
                            {content && (typeof content === 'string' ? content : content(setOpen))}
                        </div>
                    </div>
                </div>
            )}
        </span>
    )
}

export const ActiveBackground = ({ mobile = false }) => {
    return (
        <span
            className={`absolute inset-0 left-0 h-[calc(100%+1px)] w-full bg-light before:absolute
                before:top-0 before:left-0 before:z-10 before:h-full before:w-[10px] before:border-r before:border-light before:bg-accent after:absolute after:top-0 after:right-0
                after:h-full after:w-[10px] after:border-l after:border-light after:bg-accent dark:bg-dark dark:before:border-dark dark:before:bg-accent-dark dark:after:border-dark dark:after:bg-accent-dark ${
                    mobile
                        ? 'top-[-1px] before:rounded-tr-lg before:border-t after:rounded-tl-lg after:border-t'
                        : 'before:rounded-br-lg before:border-b after:rounded-bl-lg after:border-b'
                }`}
        >
            <span
                className={`absolute ${
                    mobile ? 'top-0' : 'bottom-0'
                } border-bg-light dark:border-bg-dark left-0 w-full border-b`}
            />
        </span>
    )
}

export const MenuItem = ({ url, color, icon, name, initialScrollTo, overflowing, mobile, active, onClick }) => {
    const ref = useRef<HTMLLIElement>(null)
    const Icon = icons[icon]

    useEffect(() => {
        if (initialScrollTo) ref?.current?.scrollIntoView({ block: 'nearest', inline: 'center' })
    }, [overflowing])

    const handleClick = () => {
        onClick?.()
        ref?.current?.scrollIntoView({ block: 'nearest', inline: 'center' })
    }

    return (
        <li ref={ref}>
            <Link
                onClick={handleClick}
                to={url}
                className={`group relative mb-1 flex snap-center items-center rounded px-2 pt-1.5 pb-1 ${
                    active
                        ? ''
                        : 'border border-b-3 border-transparent hover:translate-y-[-1px] active:translate-y-[1px] active:transition-all md:hover:border-light dark:md:hover:border-dark'
                }`}
            >
                <span className={`mr-2 h-6 w-6 text-${color}`}>{Icon && <Icon />}</span>
                <span
                    className={`whitespace-nowrap text-sm text-white ${
                        active ? 'font-bold opacity-100' : 'font-semibold opacity-60 group-hover:opacity-100'
                    }`}
                >
                    {name}
                </span>
                <span
                    className={`absolute ${
                        mobile ? 'top-[-4px]' : '-bottom-2'
                    } left-0 w-full rounded-full border-b-[1.5px] transition-colors ${
                        active ? `border-${color}` : `border-transparent`
                    }`}
                />
            </Link>
        </li>
    )
}

export const InternalMenu = ({ className = '', mobile = false, color, menu, activeIndex, scrollOnRender = true }) => {
    const ref = useRef<HTMLUListElement>(null)
    const [overflowing, setOverflowing] = useState(false)
    function handleResize() {
        setOverflowing((ref?.current && ref?.current.scrollWidth > ref?.current.clientWidth) || false)
    }
    const result = prepareData({
        props: menu,
    })
    useEffect(() => {
        window.addEventListener('resize', handleResize)
        handleResize()
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    return menu?.length > 0 ? (
        <ul
            style={{ justifyContent: overflowing ? 'start' : 'center' }}
            ref={ref}
            className={`relative m-0 flex snap-x list-none space-x-4 overflow-x-auto overflow-y-hidden border-b border-light px-4 pt-1 dark:border-dark ${className}`}
        >
            {menu.map((menuItem, index) => {
                return (
                    <MenuItem
                        color={color}
                        key={index}
                        {...menuItem}
                        initialScrollTo={scrollOnRender && activeIndex === index}
                        overflowing={overflowing}
                        mobile={mobile}
                        active={menu[activeIndex]?.name === menuItem.name}
                    />
                )
            })}
        </ul>
    ) : null
}

export const keyboardShortcut =
    'box-content p-[5px] border border-b-2 border-gray-accent-light dark:border-gray-accent-light/40 rounded-[3px] inline-flex text-black/35 dark:text-white/40 text-code text-xs'

export const Mobile = () => {
    const layoutData = useLayoutData() || {}
    const { menu, parent, internalMenu, activeInternalMenu } = layoutData

    return (
        <div className="fixed bottom-0 z-[9999] w-full lg:hidden">
            <InternalMenu
                mobile
                className="mb-[-1px] border-t bg-light dark:bg-dark"
                menu={internalMenu}
                activeIndex={internalMenu?.findIndex((menu) => menu === activeInternalMenu)}
            />
            <ul className="m-0 grid list-none grid-cols-5 gap-[2px] border-t border-border bg-accent px-2 dark:border-dark dark:bg-accent-dark">
                {menu?.length > 0 &&
                    menu.map((menuItem, index) => {
                        const active = menuItem.name === parent?.name
                        const { name, url, icon } = menuItem
                        const Icon = icons[icon]
                        return (
                            <li className="h-full" key={index}>
                                <Link
                                    to={url}
                                    className={`relative flex flex-col items-center space-y-1 px-4 py-4 text-[12.5px] font-medium ${
                                        active
                                            ? 'mx-[-10px] bg-light px-[calc(1rem_+_10px)] font-bold dark:bg-dark'
                                            : 'opacity-70 hover:opacity-100'
                                    }`}
                                >
                                    {active && <ActiveBackground mobile />}
                                    <span className={`relative !m-0 inline-block h-5 w-5`}>{Icon && <Icon />}</span>
                                    <span className="relative">{name}</span>
                                </Link>
                            </li>
                        )
                    })}
            </ul>
        </div>
    )
}
